import type { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type {
  PaymentError,
  PaymentGroup,
  PaymentMethod,
  PaymentMethodConfig,
} from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types/i18n'

import type { MaybeI18nDefinition } from '../../../utils/maybeI18nDefinition'

/**
 * Result of a client-side payment-method availability check.
 */
export type PaymentMethodAvailabilityStatus =
  | {
      isAvailable: false
    }
  | {
      /**
       * This method can be offered as a payment option in this context.
       */
      isAvailable: true

      /**
       * Whether completing payment may require an additional action from the user
       * with the payment provider, such as logging in or providing additional information.
       *
       * For example:
       * - Apple Pay returns "paymentCredentialStatusUnknown"
       * - Google Pay availability check times out and we cannot determine if the user has a card on file or not
       * - PayPal, Klarna, etc. where we have no way to know if the user has an account or not.
       */
      mayRequireAdditionalUserActionWithPaymentProvider: boolean
    }

export const PaymentMethodAvailabilityStatus = {
  UNAVAILABLE: { isAvailable: false },
  AVAILABLE: {
    isAvailable: true,
    mayRequireAdditionalUserActionWithPaymentProvider: false,
  },
  MAY_REQUIRE_ADDITIONAL_USER_ACTION: {
    isAvailable: true,
    mayRequireAdditionalUserActionWithPaymentProvider: true,
  },
} as const satisfies Record<string, PaymentMethodAvailabilityStatus>

export type PaymentMethodAvailabilityOptions = {
  isExpressCheckout?: boolean
}

export type PaymentMethodHardcodedConfigBase = {
  /**
   * Payment method name
   */
  name: MaybeI18nDefinition

  /**
   * Option label in payment page
   */
  label?: I18nDefinition

  /**
   * A function, that, if present, will be called in the browser,
   * to determine the payment method availability state.
   */
  getAvailabilityInBrowser?: (
    config: PaymentMethodConfig,
  ) => Promise<PaymentMethodAvailabilityStatus>
}

export type PaymentMethodWithContextHardcodedConfig<T> = Omit<
  PaymentMethodHardcodedConfigBase,
  'getAvailabilityInBrowser'
> & {
  /**
   * A function, that, if present, will be called on setup hook
   * to initialize the payment method context.
   * This is useful if a payment method needs to use composables to determine its availability state
   */
  useAvailabilityContext: (
    config: PaymentMethod,
    options?: PaymentMethodAvailabilityOptions,
  ) => T

  /**
   * Override the default availability check, add availability context
   */
  getAvailabilityInBrowser?: (
    config: PaymentMethodConfig,
    context?: T,
  ) => Promise<PaymentMethodAvailabilityStatus>
}

export type PaymentMethodWithInstallmentHardcodedConfig =
  PaymentMethodHardcodedConfigBase & {
    installmentCount: number
    ratesByCountry?: Partial<Record<MarketCountryCode, string>>
  }

export type PaymentMethodHardcodedConfig<T = unknown> =
  | PaymentMethodHardcodedConfigBase
  | PaymentMethodWithContextHardcodedConfig<T>
  | PaymentMethodWithInstallmentHardcodedConfig

export type PaymentGroupHardcodedConfig = {
  label: MaybeI18nDefinition

  /**
   * Whether the group has a custom icon
   * `true` will expect an icon in
   * (/img/payment/groups-v2/), while `false` will re-use either network icons
   * (/img/payment/networks-v2/), or methods icons (/img/payment/methods-v2/).
   */
  hasCustomIcon: boolean

  // TODO: check if we can remove this
  external?: boolean

  /**
   * Messages to display in the payment page
   */
  messages: I18nDefinition[]

  providerName: string | I18nDefinition

  errorMessages?: Partial<Record<PaymentError, I18nDefinition>>

  legalNotices?: Partial<
    Record<
      MarketCountryCode,
      {
        definition: I18nDefinition
        links: Record<string, { definition: I18nDefinition; url: string }>
        values: Record<string, unknown>
      }
    >
  >
}

export type PaymentGroupDynamicHardcodedConfigOptions = {
  methods: PaymentMethod[]
}

export type PaymentNetworkHardcodedConfig = {
  name: MaybeI18nDefinition
}

export type PaymentGroupData = {
  id: PaymentGroup
  enabledMethods: PaymentMethod[]
  hasManyEnabledMethods: boolean
  hasMessages: boolean
  isEnabled: boolean
  label: MaybeI18nDefinition
  methods: PaymentMethod[]
  config: PaymentGroupHardcodedConfig
}
